import { useEffect } from 'react';

import { Redirect, useLocalSearchParams, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import { CardModal } from 'components/card-modal';
import { useMaybeGetLegacyRedirect } from 'components/layout/use-maybe-get-legacy-redirect';
import { toaster } from 'components/toaster/toaster';
import { useLoyaltyRewardsList } from 'hooks/use-loyalty-rewards-list';
import { useAuthRequiredInModal } from 'pages/authentication/components/auth-required';
import IncentiveDetails from 'pages/loyalty/loyalty-incentives-components/incentive-details/incentive-details';
import LoyaltyLoader from 'pages/loyalty/loyalty-loader/loyalty-loader';

export default function RewardDetail() {
  // lots of legacy redirects start with /rewards/[notReallyAnID] so we need this here and the 404 handler
  const redirectUrl = useMaybeGetLegacyRedirect();

  if (!redirectUrl) {
    // hook violation fine due to this redirecting if this code happens
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAuthRequiredInModal(); // handles deep link to reward w/o being logged in
  }

  const { formatMessage } = useIntl();
  const router = useRouter();

  const { rewardId } = useLocalSearchParams<{ rewardId: string }>();
  const { sanityRewardsMap, engineRewardsMap } = useLoyaltyRewardsList();

  const selectedReward = sanityRewardsMap?.[rewardId];
  const isLoading = !sanityRewardsMap || !engineRewardsMap;

  // close with error toast if incentive can't be found
  useEffect(() => {
    if (redirectUrl) {
      return;
    }

    if (!isLoading && !selectedReward) {
      if (!router.canDismiss()) {
        return; // nav is loading still on a deep link
      }
      router.dismiss();
      toaster.negative(formatMessage({ id: 'nonAvailableReward' }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, selectedReward, redirectUrl]);

  if (redirectUrl) {
    return <Redirect href={redirectUrl} />;
  }

  if (isLoading || !selectedReward) {
    return (
      <CardModal alwaysShowHeader isLoading disableGlobalAppModals>
        <LoyaltyLoader />
      </CardModal>
    );
  }

  return (
    <IncentiveDetails engineIncentivesMap={engineRewardsMap} selectedIncentive={selectedReward} />
  );
}
